import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/config';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReportPreview = ({ reportKey, inputSchema }) => {
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [params, setParams] = useState({});
  const [formData, setFormData] = useState({});

  useEffect(() => {
    // Initialize form data from input schema
    if (inputSchema && inputSchema.properties) {
      const initialData = {};
      Object.keys(inputSchema.properties).forEach(key => {
        const prop = inputSchema.properties[key];
        if (prop.default !== undefined) {
          initialData[key] = prop.default;
        } else if (prop.type === 'array') {
          initialData[key] = [];
        } else if (prop.type === 'object') {
          initialData[key] = {};
        } else {
          initialData[key] = '';
        }
      });
      setFormData(initialData);
    }
  }, [inputSchema]);

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePreview = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Build params from form data, filtering out empty values
      const requestParams = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (value !== '' && value !== null && value !== undefined) {
          if (Array.isArray(value) && value.length === 0) {
            return; // Skip empty arrays
          }
          requestParams[key] = value;
        }
      });

      const response = await axios.post(
        API_ENDPOINTS.REPORTS_PREVIEW,
        {
          report_key: reportKey,
          params: requestParams
        },
        { headers }
      );

      // Handle standardized response format: { status: "ok", data: {...} }
      // or direct data format
      let data = null;
      if (response.data?.status === 'ok' && response.data?.data) {
        data = response.data.data;
      } else if (response.data?.status === 'error') {
        throw new Error(response.data?.message || 'Failed to generate preview');
      } else {
        data = response.data?.data || response.data;
      }
      
      setPreviewData(data);
      setParams(requestParams);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.response?.data?.error ||
                     err?.message || 
                     'Failed to generate preview';
      setError(errorMsg);
      console.error('Error generating preview:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderFormField = (key, property) => {
    const value = formData[key] || '';
    const label = property.title || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    switch (property.type) {
      case 'string':
        if (property.enum) {
          return (
            <div key={key} className="space-y-2">
              <label className="text-sm font-medium text-foreground">{label}</label>
              <select
                value={value}
                onChange={(e) => handleInputChange(key, e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select {label}</option>
                {property.enum.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          );
        }
        if (property.format === 'date') {
          return (
            <div key={key} className="space-y-2">
              <label className="text-sm font-medium text-foreground">{label}</label>
              <input
                type="date"
                value={value}
                onChange={(e) => handleInputChange(key, e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          );
        }
        return (
          <div key={key} className="space-y-2">
            <label className="text-sm font-medium text-foreground">{label}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(key, e.target.value)}
              placeholder={property.placeholder || `Enter ${label.toLowerCase()}`}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        );

      case 'number':
      case 'integer':
        return (
          <div key={key} className="space-y-2">
            <label className="text-sm font-medium text-foreground">{label}</label>
            <input
              type="number"
              value={value}
              onChange={(e) => handleInputChange(key, e.target.value ? Number(e.target.value) : '')}
              placeholder={property.placeholder || `Enter ${label.toLowerCase()}`}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        );

      case 'boolean':
        return (
          <div key={key} className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleInputChange(key, e.target.checked)}
              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
            />
            <label className="text-sm font-medium text-foreground">{label}</label>
          </div>
        );

      case 'array':
        // Simple array input - can be enhanced
        return (
          <div key={key} className="space-y-2">
            <label className="text-sm font-medium text-foreground">{label}</label>
            <input
              type="text"
              value={Array.isArray(value) ? value.join(', ') : value}
              onChange={(e) => {
                const arr = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                handleInputChange(key, arr);
              }}
              placeholder="Comma-separated values"
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Parameters Form */}
      {inputSchema && inputSchema.properties && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Report Parameters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(inputSchema.properties).map(key => {
              const property = inputSchema.properties[key];
              return renderFormField(key, property);
            })}
          </div>
          <div className="mt-6 flex space-x-3">
            <Button
              onClick={handlePreview}
              disabled={isLoading}
              iconName="Search"
              iconPosition="left"
            >
              {isLoading ? 'Generating Preview...' : 'Generate Preview'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const initialData = {};
                Object.keys(inputSchema.properties).forEach(key => {
                  const prop = inputSchema.properties[key];
                  if (prop.default !== undefined) {
                    initialData[key] = prop.default;
                  } else {
                    initialData[key] = '';
                  }
                });
                setFormData(initialData);
                setPreviewData(null);
                setError('');
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2 text-destructive">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Preview Data */}
      {previewData && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Preview Results</h3>
            {previewData.summary && (
              <div className="text-sm text-muted-foreground">
                Total: {previewData.summary.count || 0} records
              </div>
            )}
          </div>

          {/* Summary */}
          {previewData.summary && (
            <div className="mb-4 p-4 bg-muted rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.keys(previewData.summary).map(key => (
                  <div key={key}>
                    <div className="text-xs text-muted-foreground uppercase">{key.replace(/_/g, ' ')}</div>
                    <div className="text-lg font-semibold text-foreground">
                      {typeof previewData.summary[key] === 'number' 
                        ? previewData.summary[key].toLocaleString()
                        : previewData.summary[key]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Table */}
          {previewData.columns && previewData.rows && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    {previewData.columns.map((col, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-3 text-left text-sm font-semibold text-foreground"
                      >
                        {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.length > 0 ? (
                    previewData.rows.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className="border-b border-border hover:bg-muted/50 transition-colors"
                      >
                        {previewData.columns.map((col, colIdx) => (
                          <td key={colIdx} className="px-4 py-3 text-sm text-foreground">
                            {row[col] !== null && row[col] !== undefined
                              ? typeof row[col] === 'number'
                                ? row[col].toLocaleString()
                                : String(row[col])
                              : '-'}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={previewData.columns.length}
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* No columns/rows structure */}
          {!previewData.columns && !previewData.rows && (
            <div className="text-center py-8 text-muted-foreground">
              {JSON.stringify(previewData, null, 2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportPreview;

